/**
 * A guest's sign-in that vanished from storage (guest-session-loss, Plan B).
 * On a refused guest write or a failed mint: record what the phone sees
 * (the permanent failure recorder — fires only on failure, never logs a token
 * or contact data), then put the sign-in back if this page still holds it.
 * The caller retries once on `true`; nothing here retries or hides a failure.
 */
import { supabase, getAuthUserId, getAuthSignedInAt, restoreLostSession } from '../supabase';
import { logEvent } from '../analytics/eventLogger';

const storedSignIn = () => {
  try {
    const key = Object.keys(localStorage).find(
      (k) => k.startsWith('sb-') && k.endsWith('-auth-token')
    );
    if (!key) return { present: false, userId: null };
    const userId = JSON.parse(localStorage.getItem(key) || 'null')?.user?.id || null;
    return { present: true, userId };
  } catch (e) {
    return { present: null, userId: null, readError: e?.message || 'unreadable' };
  }
};

/**
 * The failure recorder: one `guest_session_diag` event with the phone's
 * sign-in state at the moment a guest write or mint failed.
 * @param {string} trigger - 'no_session_write' | 'mint_failed'
 * @param {object} [context]
 */
export const recordGuestSessionFailure = async (trigger, context = {}) => {
  try {
    const memoryUserId = getAuthUserId();
    const stored = storedSignIn();
    let fresh = { hasSession: null, error: null, userId: null };
    try {
      const { data, error } = await supabase.auth.getSession();
      fresh = {
        hasSession: !!data?.session,
        error: error?.message || null,
        userId: data?.session?.user?.id || null,
      };
    } catch (e) {
      fresh.error = e?.message || 'getSession threw';
    }
    const signedInAt = getAuthSignedInAt();
    // Recorded while the sign-in is gone, so the event goes signed out and
    // must name no profile (the server refuses that); the room in context finds it.
    await logEvent(
      'guest_session_diag',
      {
        trigger,
        ...context,
        memory_has_user: !!memoryUserId,
        stored_key_present: stored.present,
        stored_same_user: stored.userId ? stored.userId === memoryUserId : null,
        stored_read_error: stored.readError || null,
        fresh_has_session: fresh.hasSession,
        fresh_same_user: fresh.userId ? fresh.userId === memoryUserId : null,
        fresh_error: fresh.error,
        visibility: typeof document !== 'undefined' ? document.visibilityState : null,
        standalone:
          !!window.navigator?.standalone ||
          !!window.matchMedia?.('(display-mode: standalone)')?.matches,
        seconds_since_sign_in: signedInAt ? Math.round((Date.now() - signedInAt) / 1000) : null,
      },
      { withoutProfile: true }
    );
  } catch (e) {
    console.warn('[SESSION_RECOVERY] could not record:', e?.message);
  }
};

/**
 * Record the failure, then try to put the sign-in back.
 * @returns {Promise<boolean>} true = restored; the caller retries once
 */
export const recoverGuestSession = async (trigger, context = {}) => {
  await recordGuestSessionFailure(trigger, context);
  let restored = false;
  try {
    restored = await restoreLostSession();
  } catch (e) {
    console.warn('[SESSION_RECOVERY] restore failed:', e?.message);
  }
  // Still inside a failed save (e.g. a mint whose profile row isn't on the
  // server yet), so the event names no profile; the room in context finds it
  if (restored)
    logEvent('guest_session_restored', { trigger, ...context }, { withoutProfile: true });
  return restored;
};
